import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@web-frontend/shared/services/auth/auth.service';
import { AlertService } from '@web-frontend/shared/helpers/alert';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs/operators';
import { authRoutes } from '../../../auth/auth-routing.module';

@Component({
  selector: 'roma-pin-kiosk',
  templateUrl: './pin-kiosk.component.html',
  styles: [
    `
      .pin-kiosk-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background: white;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
      }
      .fullscreen-prompt {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.55);
      }
      .fullscreen-prompt__card {
        background: white;
        border-radius: 12px;
        padding: 32px 40px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        max-width: 360px;
        text-align: center;
      }
      .fullscreen-prompt__text {
        font-size: 1rem;
        color: #374151;
        margin: 0;
      }
      .fullscreen-prompt__btn {
        background: #4f46e5;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 10px 28px;
        font-size: 0.95rem;
        cursor: pointer;
        font-weight: 500;
      }
      .fullscreen-prompt__btn:hover {
        background: #4338ca;
      }
    `,
  ],
})
export class PinKioskComponent implements OnInit, OnDestroy {
  showFullscreenPrompt = false;

  constructor(
    private _cdr: ChangeDetectorRef,
    private _authService: AuthService,
    private _alertService: AlertService,
    private _i18n: TranslateService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this.requestFullscreen().catch(() => {
      this.showFullscreenPrompt = true;
      this._cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.exitFullscreen().catch(() => undefined);
  }

  enterFullscreen(): void {
    this.requestFullscreen()
      .then(() => {
        this.showFullscreenPrompt = false;
        this._cdr.detectChanges();
      })
      .catch(() => {
        this.showFullscreenPrompt = true;
        this._cdr.detectChanges();
      });
  }

  goToAdmin(): void {
    this.exitFullscreen()
      .catch(() => undefined)
      .then(() => this._router.navigate(['/admin/clock-hours/admin']));
  }

  confirmAdminLogout(): void {
    const tag = this._i18n.instant('login.logout');
    const confirmButtonText = this._i18n.instant('login.logoutConfirm');
    const cancelButtonText = this._i18n.instant('login.logoutCancel');

    this._alertService
      .sure(tag, '', true, confirmButtonText, cancelButtonText)
      .pipe(
        map((res: { value: boolean }) => res.value),
        filter((res: boolean) => !!res),
        take(1)
      )
      .subscribe(() => {
        this.exitFullscreen()
          .catch(() => undefined)
          .then(() => {
            this._authService.logout();
            this._router.navigateByUrl(authRoutes.login);
          });
      });
  }

  private requestFullscreen(): Promise<void> {
    type FullscreenEl = Element & {
      webkitRequestFullscreen?: () => Promise<void>;
      mozRequestFullScreen?: () => Promise<void>;
    };
    const el = document.documentElement as FullscreenEl;
    const fn =
      el.requestFullscreen?.bind(el) ??
      el.webkitRequestFullscreen?.bind(el) ??
      el.mozRequestFullScreen?.bind(el);
    return fn ? fn() : Promise.reject(new Error('Fullscreen not supported'));
  }

  private exitFullscreen(): Promise<void> {
    type FullscreenDoc = Document & {
      webkitExitFullscreen?: () => Promise<void>;
      mozCancelFullScreen?: () => Promise<void>;
      webkitFullscreenElement?: Element | null;
      mozFullScreenElement?: Element | null;
    };
    const doc = document as FullscreenDoc;
    const isFullscreen = !!(
      doc.fullscreenElement ??
      doc.webkitFullscreenElement ??
      doc.mozFullScreenElement
    );
    if (!isFullscreen) return Promise.resolve();
    const fn =
      doc.exitFullscreen?.bind(doc) ??
      doc.webkitExitFullscreen?.bind(doc) ??
      doc.mozCancelFullScreen?.bind(doc);
    return fn ? fn() : Promise.resolve();
  }
}
